import { apiClient } from '../config/api';

// Types
export interface TrainingCourse {
  id: string;
  title: string;
  description: string;
  category?: string;
  difficultyLevel: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
  estimatedDuration: number;
  learningObjectives: string[];
  thumbnailUrl?: string;
  videoUrl?: string;
  captionUrl?: string | null;
  transcriptText?: string | null;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isRequired: boolean;
  isRecommended: boolean;
  passingScore: number;
  enrollmentCount?: number;
  certificateCount?: number;
  quizQuestionCount?: number;
  userProgress?: CourseProgress;
  createdAt: string;
  updatedAt: string;
}

export interface CourseProgress {
  id: string;
  userId: string;
  courseId: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  videoProgress: number;
  videoDuration: number;
  currentPosition: number;
  quizScore?: number;
  quizPassed: boolean;
  attempts: number;
  startedAt?: string;
  completedAt?: string;
  lastAccessedAt: string;
}

export interface QuizQuestion {
  id: string;
  question: string;
  questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'MULTIPLE_SELECT';
  options: Array<{ id: string; text: string }>;
  points: number;
}

export interface QuizSubmission {
  answers: Array<{
    questionId: string;
    answer: string | string[];
  }>;
}

export interface QuizResult {
  id: string;
  score: number;
  passed: boolean;
  attemptNumber: number;
  correctAnswers: number;
  totalQuestions: number;
  results: Array<{
    questionId: string;
    isCorrect: boolean;
    userAnswer: string | string[];
    correctAnswer: string | string[];
    explanation?: string;
  }>;
}

export interface Certificate {
  id: string;
  certificateNumber: string;
  courseName: string;
  completionDate: string;
  expiryDate?: string;
  isRevoked: boolean;
  isVisibleOnProfile: boolean;
  pdfUrl?: string;
  course?: {
    title: string;
    category?: string;
  };
}

// Training Service
class TrainingService {
  // Course Management
  async getCourses(params?: {
    category?: string;
    difficultyLevel?: string;
    isRequired?: boolean;
    isRecommended?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    courses: TrainingCourse[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const endpoint = `/training/courses${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      courses: TrainingCourse[];
      pagination: any;
    }>(endpoint);
    
    return response.data;
  }

  async getCourse(courseId: string): Promise<TrainingCourse> {
    const response = await apiClient.get<TrainingCourse>(`/training/courses/${courseId}`);
    return response.data;
  }

  async enrollInCourse(courseId: string): Promise<CourseProgress> {
    const response = await apiClient.post<CourseProgress>(`/training/courses/${courseId}/enroll`);
    return response.data;
  }

  async updateProgress(
    courseId: string,
    data: {
      videoProgress?: number;
      currentPosition?: number;
      completedSections?: string[];
    }
  ): Promise<CourseProgress> {
    const response = await apiClient.put<CourseProgress>(
      `/training/courses/${courseId}/progress`,
      data
    );
    return response.data;
  }

  async getMyProgress(): Promise<CourseProgress[]> {
    const response = await apiClient.get<CourseProgress[]>('/training/progress');
    return response.data;
  }

  // Quiz Management
  async getQuiz(courseId: string): Promise<{ questions: QuizQuestion[] }> {
    const response = await apiClient.get<{ questions: QuizQuestion[] }>(
      `/training/courses/${courseId}/quiz`
    );
    return response.data;
  }

  async submitQuiz(courseId: string, submission: QuizSubmission): Promise<QuizResult> {
    const response = await apiClient.post<QuizResult>(
      `/training/courses/${courseId}/quiz/submit`,
      submission
    );
    return response.data;
  }

  // Certificate Management
  async generateCertificate(courseId: string): Promise<Certificate> {
    const response = await apiClient.post<Certificate>('/certificates/generate', { courseId });
    return response.data;
  }

  async getMyCertificates(): Promise<Certificate[]> {
    const response = await apiClient.get<Certificate[]>('/certificates/my-certificates');
    return response.data;
  }

  async verifyCertificate(certificateNumber: string): Promise<{
    valid: boolean;
    certificate?: Certificate;
    reason?: string;
  }> {
    const response = await apiClient.get<{
      valid: boolean;
      certificate?: Certificate;
      reason?: string;
    }>(`/certificates/verify/${certificateNumber}`);
    return response.data;
  }

  async updateCertificateVisibility(
    certificateId: string,
    isVisible: boolean
  ): Promise<Certificate> {
    const response = await apiClient.put<Certificate>(
      `/certificates/${certificateId}/visibility`,
      { isVisibleOnProfile: isVisible }
    );
    return response.data;
  }

  async downloadCertificate(certificateId: string): Promise<{ pdfUrl?: string; certificateNumber: string }> {
    const response = await apiClient.get<{ pdfUrl?: string; certificateNumber: string }>(
      `/certificates/${certificateId}/download`
    );
    return response.data;
  }
}

export const trainingService = new TrainingService();

